import { Box, Card, type CardProps, Grid, Typography } from '@mui/material';
import { ReportHeader } from '../ReportHeader';
import { ExportCsv } from '../ExportCsv';
import { useStyles } from '../styles/Report.styles';
import { useState } from 'react';
import { ReportToggleTypes } from '@consts/reports';
import { Chart } from '../Chart';
import { ToggleChartType } from '../ToggleChartType';
import { SelectRange } from '../SelectRange';
import { ReportTable } from './ReportTable';
import { ErrorBoundary } from 'react-error-boundary';
import { ReportErrorBoundary } from './ReportErrorBoundary';
import { LoadingOverlay } from '../LoadingOverlay';

import type { REPORT_RANGE } from '@consts/reports';
import type { TableOptions } from '@/types/table';

export const LoadingCard = ({ loading = false, sx, children, ...props }: CardProps & { loading?: boolean }) => (
    <Card elevation={5} sx={{ position: 'relative', display: 'flex', flexDirection: 'column', ...sx }} {...props}>
        {children}

        {loading && <LoadingOverlay />}
    </Card>
);

interface BaseReportProps {
    setRange?: (range: REPORT_RANGE) => void;
    range?: string;
    rangeText?: string;
    chartOptions?: any;
    tableOptions?: TableOptions;
    hasData: boolean;
    map?: boolean;
    loading: boolean;
    title: string;
    description?: string;
    note?: string;
    splitReportElement?: React.ReactNode;
}

export const BaseReport = ({
    setRange,
    loading,
    hasData,
    tableOptions,
    rangeText,
    range,
    map,
    chartOptions,
    title,
    description,
    note,
    splitReportElement,
}: BaseReportProps) => {
    const classes = useStyles();

    const [chartType, setChartType] = useState<ReportToggleTypes>(ReportToggleTypes.ToggleChartType);

    return (
        <ErrorBoundary fallback={<ReportErrorBoundary />}>
            <LoadingCard className={classes.root} loading={loading}>
                <Grid container direction='row' className={classes.wrapper}>
                    <Grid container direction={'row'} columnGap={2}>
                        <Grid item xs={12} md={6} lg={6} sm={12}>
                            <ReportHeader title={title} rangeText={rangeText} />
                        </Grid>

                        <Grid
                            item
                            display={'flex'}
                            xs={12}
                            md={6}
                            sm={12}
                            lg={6}
                            className={classes.actions}
                            rowGap={2}
                            columnGap={2}
                            flexWrap={'wrap'}>
                            {Boolean(setRange) && Boolean(range) && (
                                <SelectRange range={range as any} updateRange={setRange as any} />
                            )}

                            {Boolean(tableOptions) && Boolean(chartOptions) && (
                                <ToggleChartType
                                    type={chartType}
                                    updateType={(type: ReportToggleTypes) => setChartType(type)}
                                />
                            )}

                            {tableOptions && (
                                <ExportCsv headers={tableOptions.headers} rows={tableOptions.data} fileName={title} />
                            )}
                        </Grid>
                    </Grid>

                    <Grid item xs={12} className={classes.description}>
                        <Typography>
                            {description}
                            <Box component='span' fontWeight='bold'>
                                {note}
                            </Box>
                        </Typography>
                    </Grid>

                    <Grid item xs={12} justifyContent={'center'}>
                        {Boolean(chartOptions) && chartType === ReportToggleTypes.ToggleChartType && (
                            <Chart
                                map={Boolean(map)}
                                options={chartOptions}
                                loading={false}
                                hasData={hasData}
                                height={chartOptions?.chart?.height || 300}
                                splitReportElement={splitReportElement}
                            />
                        )}

                        {tableOptions && (chartType === ReportToggleTypes.ToggleTableType || !chartOptions) && (
                            <ReportTable
                                reportName={title}
                                tableOptions={tableOptions}
                                loading={false}
                                height={chartOptions?.chart?.height || 300}
                            />
                        )}
                    </Grid>
                </Grid>
            </LoadingCard>
        </ErrorBoundary>
    );
};
